import { useColorMode as useColorModeOriginal, useColorModeValue } from '@chakra-ui/react';

// light mode, dark mode
const SCHEME_NAMES = {
  DARK: 'dark',
  LIGHT: 'light',
};

// specific colors for Wonderschool scheme
const WonderColors = {
  SoftBlack: '#333',
  BlackNavy: '#1e2542',
  WhiteBlue: '#fff',
  MiddleGray1: '#9A9A9A',
  BlueGray: '#287BBB',
  BrightBlue: '#5196CB',
  WhiteGray: '#F7F7F7',
  Yellow: '#FFD681',
};

/**
 * @param {LightScheme | DarkScheme} scheme
 * @returns CSS color styles for background
 * }
 */
const makeCardColors = (scheme) => {
  return {
    yellow: scheme.yellow,
    blue: scheme.btnPrimary,
    border: scheme.inputBorder,
    yellowOpaqueBackground: '#FFD681',
  };
};
/**
 * @param {LightScheme | DarkScheme} scheme
 * @returns CSS color styles for background
 */
const makeBackgroundColors = (scheme) => {
  return {
    color: scheme.textMain,
    bg: scheme.bgMain,
  };
};
/**
 * @param {LightScheme | DarkScheme} scheme
 * @returns CSS color styles for form inputs
 */
const makeInputColors = (scheme) => {
  return {
    bg: scheme.inputBg,
    borderColor: scheme.inputBorder,
    _placeholder: {
      color: scheme.inputPlaceholder,
    },
    _focus: {
      color: scheme.inputAccent,
      borderColor: scheme.inputBorderAccent,
      _placeholder: { color: scheme.inputBorderAccent },
    },
    _hover: {
      borderColor: scheme.inputBorderAccent,
      _placeholder: {
        color: scheme.inputPlaceholderAccent,
      },
      _focus: {
        color: scheme.inputAccent,
        borderColor: scheme.inputBorderAccent,
        _placeholder: { color: scheme.inputBorderAccent },
      },
    },
    _autofill: {
      bg: scheme.inputBg + ' !important',
    },
  };
};
/**
 * @param {LightScheme | DarkScheme} scheme
 * @returns CSS color styles for anchor links
 */
const makeLinkColors = (scheme) => {
  return {
    color: scheme.link,
    _hover: { color: scheme.linkHover },
  };
};
/**
 * @param {LightScheme | DarkScheme} scheme
 * @returns CSS color styles for button inputs
 */
const makePrimaryButtonColors = (scheme) => {
  return {
    bg: scheme.btnPrimary,
    color: scheme.btnText,
    _hover: {
      bg: scheme.btnHover,
      color: scheme.btnHoverText,
    },
  };
};
/**
 * @param {LightScheme | DarkScheme} scheme
 * @returns CSS color styles for button inputs
 */
const makeSecondaryButtonColors = (scheme) => {
  return {
    bg: scheme.white,
    color: WonderColors.BlueGray,
    _hover: {
      bg: WonderColors.WhiteGray,
      color: WonderColors.BlueGray,
    },
  };
};
/**
 * @param {LightScheme | DarkScheme} scheme
 * @returns CSS color styles for button inputs
 */
const makeSpinnerColors = (scheme) => {
  return {
    color: scheme.spinnerColor,
  };
};
/**
 * @param {LightScheme | DarkScheme} scheme
 * @param {string} optKey lets you move the color from text color to background, etc
 * @returns CSS color styles for error elements
 */
const makeErrorColors = (scheme, optKey = 'color') => {
  return {
    [optKey]: scheme.invalidColor,
  };
};
/**
 * @param {LightScheme | DarkScheme} scheme
 * @param {string} optKey lets you move the color from text color to background, etc
 * @returns CSS color styles for subtle, muted elements
 */
const makeSubtleColors = (scheme, optKey = 'color') => {
  return {
    [optKey]: scheme.textLight,
  };
};

// ----------------------------------------------------------------
// utility to add style builders to individual themes. use `curryFunctions` to
// curry with a specific theme and bind to the theme itself.
const functions = {
  makeCardColors,
  makeBackgroundColors,
  makeInputColors,
  makeLinkColors,
  makePrimaryButtonColors,
  makeSecondaryButtonColors,
  makeSpinnerColors,
  makeErrorColors,
  makeSubtleColors,
};
const curryFunctions = (functions, scheme) => {
  const list = Object.entries(functions).map(([name, fn]) => {
    return [name, (...args) => fn(scheme, ...args)];
  });
  return list.reduce((prev, [name, fn]) => {
    return {
      ...prev,
      [name]: fn,
    };
  }, {});
};
// ----------------------------------------------------------------
// ****************************************************************
const LightScheme = {
  bgMain: 'gray.100',
  bgAccent: 'gray.50',
  bgDir: 'tl',
  textMain: WonderColors.SoftBlack,
  textLight: 'gray.400',
  inputBg: WonderColors.WhiteBlue,
  inputBorder: 'gray.400',
  inputText: WonderColors.MiddleGray1,
  inputPlaceholder: 'blackAlpha.400',
  inputAccent: 'black',
  inputBorderAccent: WonderColors.BlueGray,
  inputPlaceholderAccent: 'black',
  link: 'blue.500',
  linkHover: 'blue.700',
  btnPrimary: WonderColors.BlueGray,
  btnText: 'gray.100',
  btnHover: WonderColors.BrightBlue,
  btnHoverText: 'white',
  spinnerColor: WonderColors.BlueGray,
  invalidColor: 'red.500',
  white: 'white',
  yellow: WonderColors.Yellow,
};
Object.assign(LightScheme, curryFunctions(functions, LightScheme));

// ****************************************************************
const DarkScheme = {
  bgMain: 'gray.800',
  bgAccent: WonderColors.BlackNavy,
  bgDir: 'br',
  textMain: 'gray.100',
  textLight: 'whiteAlpha.500',
  inputBg: WonderColors.BlackNavy,
  inputBorder: 'whiteAlpha.500',
  inputText: 'whiteAlpha.900',
  inputPlaceholder: 'whiteAlpha.300',
  inputAccent: 'blue.500',
  inputBorderAccent: 'whiteAlpha.600',
  inputPlaceholderAccent: 'whiteAlpha.400',
  link: 'blue.300',
  linkHover: 'blue.100',
  btnPrimary: 'blue.600',
  btnText: 'gray.100',
  btnHover: 'blue.500',
  btnHoverText: 'gray.200',
  spinnerColor: 'blue.500',
  invalidColor: 'red.300',
  white: 'white',
  yellow: WonderColors.Yellow,
};
Object.assign(DarkScheme, curryFunctions(functions, DarkScheme));
// ****************************************************************
const ColorSchemes = { Light: LightScheme, Dark: DarkScheme };
// ****************************************************************

/**
 * given the color function, determines whether to use light or dark mode, and returns the css styles
 * @example useColorScheme('makeButtonColors');
 * @param {string} schemeFnName name of function that generates the styles. one of the listed ones above.
 * @param  {...any} args additional args like `optKey`
 * @returns
 */
const useColorScheme = (schemeFnName, ...args) => {
  return useColorModeValue(LightScheme[schemeFnName](...args), DarkScheme[schemeFnName](...args));
};

/**
 * use for figuring out which color mode/scheme is being used, light or dark.
 * @returns same as useColorMode (from Chakra) and adds isDark/isLight helpers
 */
const useColorMode = () => {
  const colorModeObj = useColorModeOriginal();
  return {
    ...colorModeObj,
    /**
     * @returns {boolean}
     */
    isDark: () => {
      return colorModeObj.colorMode === SCHEME_NAMES.DARK;
    },
    /**
     * @returns {boolean}
     */
    isLight: () => {
      return colorModeObj.colorMode === SCHEME_NAMES.LIGHT;
    },
  };
};

export { ColorSchemes, useColorScheme, useColorMode };
